import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate } from "react-router-dom";
import bgImage from "../../assets/backgroundimages.png";
import companyLogo from  "../../assets/Ytc_light.png";
import axios from "axios";
import config  from  '../../config';

const AdminAuth = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState("login");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const response = await axios.post(
        `${config.apiBaseUrl}/admin/auth/login`,
        {
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
        }
      );

      const data = response.data;

      if (data.success) {
        sessionStorage.setItem("token",data.userData.token);
        sessionStorage.setItem("email",data.userData.email);
        sessionStorage.setItem("role",data.userData.role);
        sessionStorage.setItem("userId",data.userData.userId);

        setSnackbar({
          open: true,
          message:
            data.message || "Login Successful",
          severity: "success",
        });

        setTimeout(() => {
          navigate("/dashboard");
        }, 1000);
      } else {
        setSnackbar({
          open: true,
          message:
            data.message || "Login Failed",
          severity: "error",
        });
      }
    } catch (error) {
      // console.error("Login Error:", error);

      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Invalid email or password",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();

    try {
      setForgotLoading(true);

      const response = await axios.post(
        `${config.apiBaseUrl}/admin/auth/forgot-password`,
        {
          email: forgotEmail.trim(),
        }
      );

      const data = response.data;

      setSnackbar({
        open: true,
        message:
          data.message ||
          (data.success
            ? "Password sent to your registered email"
            : "Something went wrong"),
        severity: data.success ? "success" : "error",
      });

      if (data.success) {
        setForgotEmail("");
        setTimeout(() => {
          setView("login");
        }, 1500);
      }
    } catch (error) {
      // console.error("Forgot Password Error:", error);

      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Unable to process request. Please try again.",
        severity: "error",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        overflow: "hidden",
        backgroundImage: `
          linear-gradient(
            rgba(5, 10, 30, 0.85),
            rgba(5, 10, 30, 0.92)
          ),
          url(${bgImage})
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: isMobile ? 0 : 2,
      }}
    >
      <Paper
        elevation={12}
        sx={{
          width: "100%",
          maxWidth: "1100px",
          height: isMobile ? "100vh" : "90vh",
          maxHeight: "720px",
          borderRadius: isMobile ? 0 : "30px",
          overflow: "hidden",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          backdropFilter: "blur(12px)",
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              flex: 1,
              background:
                "linear-gradient(135deg, #0F172A 0%, #111827 50%, #1E293B 100%)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: 6,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "-100px",
                right: "-100px",
                width: "250px",
                height: "250px",
                background: "#2563EB",
                opacity: 0.2,
                filter: "blur(120px)",
                borderRadius: "50%",
              }}
            />

            <Stack spacing={4} position="relative" zIndex={2}>
              <Box
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: "26px",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 15px 35px rgba(59,130,246,0.45)",
                  p: 1.25,
                }}
              >
                <Box
                  component="img"
                  src={companyLogo}
                  alt="Company Logo"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Call Analysis
                  <br />
                  CRM Dashboard
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    lineHeight: 1.8,
                    maxWidth: "480px",
                    fontSize: "0.98rem",
                  }}
                >
                  Manage conversations, monitor AI insights, track team
                  performance, and centralize your customer interactions in one
                  intelligent CRM system.
                </Typography>
              </Box>

              <Stack spacing={2}>
                {[
                  "AI Powered Call Analytics",
                  "Conversation Sentiment Tracking",
                  "Real-time Team Monitoring",
                  "Centralized CRM Dashboard",
                ].map((item, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg,#3B82F6,#06B6D4)",
                      }}
                    />

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.85)",
                        fontSize: "0.96rem",
                      }}
                    >
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 3, sm: 4, md: 5 },
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "420px",
            }}
          >
            {isMobile && (
              <Stack alignItems="center" spacing={2} mb={4}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: "22px",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 25px rgba(37,99,235,0.3)",
                    border: "1px solid #E5E7EB",
                    p: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={companyLogo}
                    alt="Company Logo"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Box textAlign="center">
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    color="#111827"
                  >
                    Call Analysis CRM
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6B7280",
                      mt: 1,
                    }}
                  >
                    {view === "login" ? "Login to continue" : "Reset your password"}
                  </Typography>
                </Box>
              </Stack>
            )}

            {!isMobile && view === "login" && (
              <>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{
                    color: "#111827",
                    mb: 1,
                  }}
                >
                  Welcome Back
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#6B7280",
                    mb: 4,
                  }}
                >
                  Login to access your CRM dashboard
                </Typography>
              </>
            )}

            {!isMobile && view === "forgot" && (
              <>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{
                    color: "#111827",
                    mb: 1,
                  }}
                >
                  Forgot Password
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#6B7280",
                    mb: 4,
                  }}
                >
                  Enter your registered email and we'll send your password to it
                </Typography>
              </>
            )}

            {view === "login" ? (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon
                          sx={{
                            color: "#6B7280",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      background: "#F9FAFB",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon
                          sx={{
                            color: "#6B7280",
                          }}
                        />
                      </InputAdornment>
                    ),

                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowPassword(!showPassword)
                          }
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff
                              sx={{
                                color: "#6B7280",
                              }}
                            />
                          ) : (
                            <Visibility
                              sx={{
                                color: "#6B7280",
                              }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      background: "#F9FAFB",
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 4,
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    onClick={() => setView("forgot")}
                    sx={{
                      color: "#2563EB",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    height: 56,
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                    boxShadow: "0 12px 30px rgba(37,99,235,0.35)",

                    "&:hover": {
                      background:
                        "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: "#fff" }}
                    />
                  ) : (
                    "Login to Dashboard"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleForgotPasswordSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="forgotEmail"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon
                          sx={{
                            color: "#6B7280",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 4,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      background: "#F9FAFB",
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={forgotLoading}
                  sx={{
                    height: 56,
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                    boxShadow: "0 12px 30px rgba(37,99,235,0.35)",

                    "&:hover": {
                      background:
                        "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                    },
                  }}
                >
                  {forgotLoading ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: "#fff" }}
                    />
                  ) : (
                    "Send Password"
                  )}
                </Button>

                <Button
                  fullWidth
                  onClick={() => setView("login")}
                  startIcon={<ArrowBackRoundedIcon />}
                  sx={{
                    mt: 2,
                    height: 48,
                    borderRadius: "16px",
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#6B7280",
                  }}
                >
                  Back to Login
                </Button>
              </form>
            )}
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        TransitionComponent={Fade}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAuth;